"use client";

import { useCallback, useMemo } from "react";
import { create } from "zustand";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { SecondaryLayer, SecondaryLayerSection } from "@/types";

interface SecondaryLayersStore {
  /**
   * Names of the layers the user has explicitly chosen, or null while none have
   * been chosen and the project's defaults still apply.
   *
   * Null rather than an empty array because the two mean different things: "the
   * user hasn't touched this" must not be confused with "the user turned
   * everything off", which has to stick.
   *
   * The store deliberately knows nothing about the defaults — resolving them needs
   * the project config, which is per-request, while this store is module-level.
   * `useSecondaryLayers` owns that resolution.
   */
  active: string[] | null;
  setActive: (names: string[]) => void;
}

export const useSecondaryLayersStore = create<SecondaryLayersStore>((set) => ({
  active: null,
  setActive: (names) => set({ active: names }),
}));

/** Every secondary layer across all sections, flattened. */
export function flattenSections(
  sections: SecondaryLayerSection[] | undefined
): SecondaryLayer[] {
  return (sections ?? []).flatMap((section) => section.secondaryLayers ?? []);
}

/**
 * Which layers a project starts with switched on.
 *
 * `disabled` in the CMS means "starts off", not "unavailable" — the layer is still
 * added to the style and can be toggled on. That naming is confusing enough to be
 * worth stating: a disabled layer is present but hidden.
 */
export function defaultActiveLayerNames(
  sections: SecondaryLayerSection[] | undefined
): string[] {
  return flattenSections(sections)
    .filter((layer) => !!layer.name && !layer.disabled)
    .map((layer) => layer.name as string);
}

/** The layers of a section the user is allowed to switch. */
export function toggleableNames(section: SecondaryLayerSection): string[] {
  return (section.secondaryLayers ?? [])
    .filter((layer) => layer.canBeToggled && layer.name)
    .map((layer) => layer.name as string);
}

/**
 * How many layers across the whole project can be switched at all.
 *
 * The panel is only worth offering when this is non-zero: a project can have
 * plenty of secondary layers and no way to turn any of them off, in which case
 * the tab would list rows that do nothing.
 */
export function toggleableLayerCount(
  sections: SecondaryLayerSection[] | undefined
): number {
  return (sections ?? []).reduce(
    (count, section) => count + toggleableNames(section).length,
    0
  );
}

/** Sections a project wants expanded to begin with. */
export function defaultOpenSectionIds(
  sections: SecondaryLayerSection[] | undefined
): string[] {
  return (sections ?? [])
    .filter((section) => section.defaultOpen && section.id)
    .map((section) => section.id as string);
}

/**
 * The active list after switching a whole section on or off.
 *
 * Computed in one go rather than by toggling each layer in turn: every toggle
 * reads the same rendered `active`, so a loop of them would apply only the last.
 */
export function withSection(
  active: string[],
  names: string[],
  next: boolean
): string[] {
  return next
    ? [...new Set([...active, ...names])]
    : active.filter((name) => !names.includes(name));
}

/**
 * The secondary-layer toggles.
 *
 * State lives in a store rather than the URL, matching the legacy app. A shared
 * link therefore reproduces the camera and the selected POI but not which overlays
 * were on — worth revisiting, but it's a product decision rather than a porting one.
 */
export function useSecondaryLayers() {
  const { secondaryLayers } = useProjectConfigs();
  const stored = useSecondaryLayersStore((state) => state.active);
  const setActive = useSecondaryLayersStore((state) => state.setActive);

  // Derived rather than written by an effect: an effect would render one frame
  // with nothing enabled before correcting itself, which reads as a flicker of
  // every overlay switching on.
  const active = useMemo(
    () => stored ?? defaultActiveLayerNames(secondaryLayers),
    [stored, secondaryLayers]
  );

  const isActive = useCallback(
    (name: string | undefined) => !!name && active.includes(name),
    [active]
  );

  // Toggles against the *effective* list, not the store's raw value. Toggling
  // through the store alone would treat the not-yet-chosen state as "nothing on"
  // and switch a layer that was visible by default on instead of off.
  /** Whether every switchable layer in a section is on. */
  const isSectionActive = useCallback(
    (section: SecondaryLayerSection) => {
      const names = toggleableNames(section);
      return names.length > 0 && names.every((name) => active.includes(name));
    },
    [active]
  );

  const toggleSection = useCallback(
    (section: SecondaryLayerSection) =>
      setActive(
        withSection(active, toggleableNames(section), !isSectionActive(section))
      ),
    [active, isSectionActive, setActive]
  );

  const toggle = useCallback(
    (name: string) =>
      setActive(
        active.includes(name)
          ? active.filter((entry) => entry !== name)
          : [...active, name]
      ),
    [active, setActive]
  );

  return {
    sections: secondaryLayers ?? [],
    layers: useMemo(() => flattenSections(secondaryLayers), [secondaryLayers]),
    active,
    isActive,
    isSectionActive,
    toggle,
    toggleSection,
    setActive,
  };
}
